import { fetchHtmlPageText } from "../io/fetchHtmlPage.js";

function closeHtmlUrlSingleton(): void {
  const existing = document.querySelector<HTMLDialogElement>('dialog[data-singleton-id="npp-html-url"]');
  if (existing) {
    existing.close();
    existing.remove();
  }
}

export type OpenHtmlFromUrlDialogOpts = {
  onLoaded: (resolvedUrl: string, html: string) => void;
};

/**
 * Modal: URL field + Load — fetches HTML and passes it to `onLoaded` (caller adds tab).
 */
export function openHtmlFromUrlDialog(opts: OpenHtmlFromUrlDialogOpts): void {
  closeHtmlUrlSingleton();
  const dlg = document.createElement("dialog");
  dlg.dataset.singletonId = "npp-html-url";
  dlg.className = "npp-dialog npp-dialog-search";
  dlg.innerHTML = `
    <div class="npp-dialog-form npp-dialog-search-form">
      <h2 class="npp-dialog-title">Open HTML from URL</h2>
      <p class="npp-dialog-hint">Sends the URL to the app server, which downloads the page and opens it in a new HTML tab (no browser CORS). With <code>npm run dev</code> or <code>npm run preview</code>, Vite handles this in-process—no separate fetch process. Docker uses nginx to a small Node service.</p>
      <label class="npp-dialog-row">Page URL
        <input type="url" class="npp-dialog-input" name="url" spellcheck="false" autocomplete="off" placeholder="https://example.com/page.html" />
      </label>
      <p class="npp-dialog-hint" data-role="status" style="min-height:1.25em;color:#a33;"></p>
      <div class="npp-dialog-actions">
        <button type="button" data-action="load">Load into new tab</button>
        <button type="button" data-action="cancel">Cancel</button>
      </div>
    </div>`;
  document.body.append(dlg);

  const urlIn = dlg.querySelector<HTMLInputElement>('input[name="url"]')!;
  const statusEl = dlg.querySelector<HTMLElement>('[data-role="status"]')!;
  const loadBtn = dlg.querySelector<HTMLButtonElement>('[data-action="load"]')!;
  const cancelBtn = dlg.querySelector<HTMLButtonElement>('[data-action="cancel"]')!;

  const setBusy = (busy: boolean): void => {
    loadBtn.disabled = busy;
    cancelBtn.disabled = busy;
    urlIn.readOnly = busy;
  };

  const runLoad = async (): Promise<void> => {
    statusEl.textContent = "";
    setBusy(true);
    try {
      const result = await fetchHtmlPageText(urlIn.value);
      if (!result.ok) {
        statusEl.textContent = result.message;
        return;
      }
      dlg.close();
      dlg.remove();
      opts.onLoaded(result.url, result.text);
    } finally {
      setBusy(false);
    }
  };

  loadBtn.addEventListener("click", () => void runLoad());
  cancelBtn.addEventListener("click", () => {
    dlg.close();
    dlg.remove();
  });
  urlIn.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void runLoad();
    }
  });
  dlg.addEventListener("close", () => {
    if (dlg.isConnected) dlg.remove();
  });
  dlg.showModal();
  queueMicrotask(() => urlIn.focus());
}
